import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  adminListSocialPosts,
  adminDeleteSocialPost,
  adminUpsertSocialPost,
  adminGenerateAiDrafts,
  adminGenerateDayDrafts,
  adminReviseDraftWithAi,
  adminClearSocialDrafts,
} from "@/lib/social-admin.functions";
import { listGhlSocialAccounts } from "@/lib/ghl-connect.functions";
import { publishSocialPostViaGhl } from "@/lib/social-analytics.functions";
import { SocialSubNav } from "@/components/admin/SocialSubNav";
import { MediaLightbox, SocialPreview, socialMediaUrls } from "@/components/social/SocialPreview";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles,
  Trash2,
  Send,
  Wand2,
  ShieldCheck,
  CalendarClock,
  Images,
  Filter,
  PlugZap,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { weekStartFor } from "@/lib/blog/weekly-plan";
import {
  ET_TIMEZONE,
  toEtDateTimeLocal,
  etDateTimeLocalToIso,
  etSlotOnDate,
  formatEtDateTime,
} from "@/lib/et-datetime";

export const Route = createFileRoute("/admin/social/drafts")({
  head: () => ({
    meta: [
      { title: "Social Media Drafts | Simply Approved Admin" },
      { name: "description", content: "Review weekly blog-linked social media drafts and manage scheduled posts." },
      { property: "og:title", content: "Social Media Drafts | Simply Approved Admin" },
      { property: "og:description", content: "Review weekly blog-linked social media drafts and manage scheduled posts." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: DraftsPage,
});

const MORTGAGE_SAFEGUARDS = [
  'Never says "tax-free income" — treats proceeds as loan, not income',
  'Never promises "no monthly payments" without the taxes/insurance/maintenance qualifier',
  'No urgency language ("act now", "limited time", "today only")',
  'No guaranteed outcomes ("guaranteed approval", "instant fix")',
  'Business-purpose, non-owner-occupied framing enforced',
  'Simply Approved Mortgages cited as brokerage (NMLS #2620881), not lender',
  'Broker (not lender) wording enforced on every draft',
  'No fabricated stats, rates, testimonials, or HUD numbers',
];

type Post = {
  id: string;
  body: string | null;
  platforms: string[] | null;
  status: string;
  scheduled_at: string | null;
  published_at: string | null;
  link_url: string | null;
  media_urls: string[] | null;
  external_ids: Record<string, any> | null;
  tags?: string[] | null;
  error_message: string | null;
  created_at: string;
  updated_at?: string | null;
  // Weekly blog-linked campaign metadata.
  source_blog_post_id?: string | null;
  batch_week?: string | null;
  featured_rank?: number | null;
  schedule_source?: string | null;
  schedule_rationale?: Record<string, any> | null;
  owner_edited_at?: string | null;
};

type Edit = {
  body?: string;
  scheduleAt?: string;
  instruction?: string;
  mediaUrl?: string;
  selectedAccounts?: string[];
};

type DatePreset = "week" | "next-week" | "all" | "unscheduled";

// Scheduling is always expressed in Eastern Time.
function toDateTimeLocal(iso: string | null | undefined): string {
  return toEtDateTimeLocal(iso);
}

function weekRange() {
  // Match the Saturday blog planner exactly: Saturday previews the coming
  // week; every other day shows the active Sunday–Saturday week.
  const start = weekStartFor(new Date());
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 7);
  return { start, end };
}

function dayBadge(iso: string | null | undefined) {
  if (!iso) return "Unscheduled";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unscheduled";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: ET_TIMEZONE,
    weekday: "long",
    month: "short",
    day: "numeric",
  }).format(d);
}

function targetAccountIds(post: Post): string[] {
  const ids = post.external_ids?.target_account_ids;
  return Array.isArray(ids) ? ids.filter((v): v is string => typeof v === "string") : [];
}

function recommendedTimesFor(iso: string | null | undefined) {
  const base = iso ? new Date(iso) : new Date();
  if (Number.isNaN(base.getTime())) return [];
  const times = [9, 10, 12, 15, 18];
  return times.map((hour) => ({
    label: `${hour % 12 === 0 ? 12 : hour % 12}:00 ${hour < 12 ? "AM" : "PM"} ET`,
    value: etSlotOnDate(base, hour, 0),
  }));
}

function DraftsPage() {
  const list = useServerFn(adminListSocialPosts);
  const del = useServerFn(adminDeleteSocialPost);
  const save = useServerFn(adminUpsertSocialPost);
  const gen = useServerFn(adminGenerateAiDrafts);
  const genDay = useServerFn(adminGenerateDayDrafts);
  const revise = useServerFn(adminReviseDraftWithAi);
  const clearDrafts = useServerFn(adminClearSocialDrafts);
  const listAccounts = useServerFn(listGhlSocialAccounts);
  const publishViaGhl = useServerFn(publishSocialPostViaGhl);
  const qc = useQueryClient();

  const q = useQuery({ queryKey: ["social-posts"], queryFn: () => list() });
  const accountsQ = useQuery({ queryKey: ["ghl-social-accounts"], queryFn: () => listAccounts() });
  const accounts = accountsQ.data?.ok ? accountsQ.data.accounts : [];
  const compatibleAccountIds = (post: Post) => {
    const wanted = new Set((post.platforms ?? []).map((platform) => platform.toLowerCase()));
    return accounts
      .filter((account: any) => wanted.has(String(account.platform ?? "").toLowerCase()))
      .map((account: any) => String(account.id));
  };
  const selectedAccountIds = (post: Post) => {
    const compatible = compatibleAccountIds(post);
    const edited = edits[post.id]?.selectedAccounts;
    if (edited !== undefined) return edited.filter((id) => compatible.includes(id));
    const stored = targetAccountIds(post).filter((id) => compatible.includes(id));
    // Older generated drafts stored local account UUIDs. When none of those
    // match the live provider IDs, select the post's compatible channels.
    return stored.length ? stored : compatible;
  };

  const [safeguardsOk, setSafeguardsOk] = useState(false);
  const [busy, setBusy] = useState<"" | "now" | "week">("");
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  const [reviseBusy, setReviseBusy] = useState<string | null>(null);
  // Tracks which single action is running, keyed as `${postId}:save|schedule|post`
  // so clicking one button never puts the sibling buttons into a busy state.
  const [saveBusy, setSaveBusy] = useState<string | null>(null);
  const isBusy = (id: string, action: "save" | "schedule" | "post") => saveBusy === `${id}:${action}`;
  const anyBusy = (id: string) => !!saveBusy && saveBusy.startsWith(`${id}:`);
  const [datePreset, setDatePreset] = useState<DatePreset>("week");
  const [statusFilter, setStatusFilter] = useState("draft");
  // "blog" shows the one campaign generated from this week's articles;
  // "regular" shows the one weekly regular (non-blog) Monday campaign.
  const [sourceFilter, setSourceFilter] = useState<"all" | "blog" | "regular" | "other">("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);

  const allPosts = ((q.data ?? []) as unknown as Post[]);
  const { start: weekStart, end: weekEnd } = useMemo(() => weekRange(), []);
  const allDrafts = useMemo(() => {
    return allPosts.slice().sort((a, b) => {
      const ta = a.scheduled_at ? new Date(a.scheduled_at).getTime() : new Date(a.created_at).getTime();
      const tb = b.scheduled_at ? new Date(b.scheduled_at).getTime() : new Date(b.created_at).getTime();
      return ta - tb;
    });
  }, [allPosts]);
  const isBlogCampaign = (p: Post) =>
    Boolean(p.source_blog_post_id ?? p.external_ids?.source_blog_post_id) ||
    (p.tags ?? []).includes("blog");
  const isRegularWeekly = (p: Post) =>
    p.external_ids?.source === "weekly_regular_social" || (p.tags ?? []).includes("regular");
  const categoryOf = (p: Post) =>
    (p.external_ids?.content_category as string | undefined) ?? null;
  const categoryLabelOf = (p: Post) =>
    (p.external_ids?.content_category_label as string | undefined) ?? categoryOf(p);
  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of allDrafts) {
      const c = categoryOf(p);
      if (c) set.add(c);
    }
    return Array.from(set).sort();
  }, [allDrafts]);
  const nextWeekStart = useMemo(() => new Date(weekEnd), [weekEnd]);
  const nextWeekEnd = useMemo(() => {
    const d = new Date(weekEnd);
    d.setUTCDate(d.getUTCDate() + 7);
    return d;
  }, [weekEnd]);
  const posts = useMemo(() => {
    return allDrafts.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (sourceFilter === "blog" && !isBlogCampaign(p)) return false;
      if (sourceFilter === "regular" && !isRegularWeekly(p)) return false;
      if (sourceFilter === "other" && (isBlogCampaign(p) || isRegularWeekly(p))) return false;
      if (categoryFilter !== "all" && categoryOf(p) !== categoryFilter) return false;
      if (datePreset === "unscheduled" && p.scheduled_at) return false;
      if (datePreset === "next-week") {
        if (!p.scheduled_at) return false;
        const d = new Date(p.scheduled_at);
        if (d < nextWeekStart || d >= nextWeekEnd) return false;
      }
      if (datePreset === "week") {
        // A post belongs to the week of its EFFECTIVE date — when it goes out,
        // not when it was generated. Saturday-generated drafts scheduled for
        // next week belong to next week, even though they were created this
        // week. Priority: schedule → publish time → batch week → created.
        const effective =
          p.scheduled_at ?? p.published_at ?? p.batch_week ?? p.created_at;
        const d = effective ? new Date(effective) : null;
        if (!d || Number.isNaN(d.getTime()) || d < weekStart || d >= weekEnd) return false;
      }

      return true;
    });
  }, [allDrafts, datePreset, statusFilter, sourceFilter, categoryFilter, weekEnd, weekStart, nextWeekStart, nextWeekEnd]);

  const weekDrafts = allPosts.filter((p) => {
    if (p.status !== "draft") return false;
    // Same effective-date rule as the "week" preset above.
    const effective = p.scheduled_at ?? p.published_at ?? p.batch_week ?? p.created_at;
    const d = effective ? new Date(effective) : null;
    return !!d && !Number.isNaN(d.getTime()) && d >= weekStart && d < weekEnd;
  });
  const weekByDay = weekDrafts.reduce<Record<string, number>>((acc, p) => {
    const label = dayBadge(p.scheduled_at).split(",")[0];
    acc[label] = (acc[label] ?? 0) + 1;
    return acc;
  }, {});


  const patch = (id: string, next: Partial<Edit>) =>
    setEdits((e) => ({ ...e, [id]: { ...e[id], ...next } }));

  const runGenerate = async () => {
    setBusy("now");
    try {
      const r = await gen({ data: { count: 1 } });
      toast.success(`Generated ${r.inserted} compliant draft${r.inserted === 1 ? "" : "s"} with image preview`);
      await qc.invalidateQueries({ queryKey: ["social-posts"] });
    } catch (e: any) {
      toast.error(e?.message ?? "AI generation failed");
    } finally {
      setBusy("");
    }
  };

  const runGenerateWeek = async () => {
    setBusy("week");
    try {
      const results = await Promise.allSettled(
        [0, 1, 2, 3, 4, 5, 6].map((dayOffset) => genDay({ data: { dayOffset } })),
      );
      const inserted = results.reduce(
        (n, r) => n + (r.status === "fulfilled" ? (r.value as any).inserted ?? 0 : 0),
        0,
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) toast.warning(`Generated ${inserted} — ${failed} day(s) failed. Try again to retry.`);
      else toast.success(`Generated ${inserted} Sunday–Saturday draft${inserted === 1 ? "" : "s"}`);
      await qc.invalidateQueries({ queryKey: ["social-posts"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Weekly generation failed");
    } finally {
      setBusy("");
    }
  };

  const runRevise = async (id: string, currentBody: string) => {
    const instruction = (edits[id]?.instruction ?? "").trim();
    if (!instruction) return;
    setReviseBusy(id);
    try {
      const r = await revise({ data: { id, body: currentBody, instruction } });
      patch(id, { body: r.body, instruction: "" });
      toast.success("Revised by AI");
      await qc.invalidateQueries({ queryKey: ["social-posts"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Revision failed");
    } finally {
      setReviseBusy(null);
    }
  };

  const runSave = async (
    p: Post,
    opts: { status?: "draft" | "scheduled" | "published" | "failed"; silent?: boolean } = {},
  ) => {
    if (!opts.silent) setSaveBusy(`${p.id}:save`);
    try {
      const body = edits[p.id]?.body ?? p.body ?? "";
      const scheduleAtLocal = edits[p.id]?.scheduleAt;
      const scheduled_at = scheduleAtLocal !== undefined
        ? etDateTimeLocalToIso(scheduleAtLocal)
        : p.scheduled_at ?? null;
      const media_urls = socialMediaUrls(edits[p.id]?.mediaUrl ?? p.media_urls ?? []);
      const selectedAccounts = edits[p.id]?.selectedAccounts ?? targetAccountIds(p);
      await save({
        data: {
          id: p.id,
          platforms: p.platforms ?? ["facebook", "linkedin", "instagram"],
          body,
          link_url: p.link_url ?? null,
          media_urls,
          external_ids: { ...(p.external_ids ?? {}), target_account_ids: selectedAccounts },
          status: opts.status ?? (p.status as any) ?? "draft",
          scheduled_at,
        },
      });
      if (!opts.silent) {
        toast.success("Saved");
        await qc.invalidateQueries({ queryKey: ["social-posts"] });
      }
    } catch (e: any) {
      if (!opts.silent) toast.error(e?.message ?? "Save failed");
      throw e;
    } finally {
      if (!opts.silent) setSaveBusy(null);
    }
  };

  const runSend = async (p: Post, sendNow: boolean) => {
    if (anyBusy(p.id)) return;
    const selectedAccounts = selectedAccountIds(p);
    if (selectedAccounts.length === 0) {
      toast.error("Select at least one connected GHL account first");
      return;
    }
    const scheduleAtLocal = edits[p.id]?.scheduleAt ?? toDateTimeLocal(p.scheduled_at);
    const schedule_at = sendNow ? null : etDateTimeLocalToIso(scheduleAtLocal);
    if (!sendNow && !schedule_at) {
      toast.error("Pick a schedule time first");
      return;
    }
    if (!sendNow && new Date(schedule_at!).getTime() <= Date.now()) {
      toast.error("Pick a schedule time in the future");
      return;
    }
    setSaveBusy(`${p.id}:${sendNow ? "post" : "schedule"}`);
    try {
      await runSave(p, { status: "draft", silent: true });
      await publishViaGhl({ data: { post_id: p.id, ghl_account_ids: selectedAccounts, schedule_at } });
      toast.success(sendNow ? "Sent to GHL for publishing" : "Scheduled in GHL");
      await qc.invalidateQueries({ queryKey: ["social-posts"] });
      await qc.invalidateQueries({ queryKey: ["social-overview"] });
    } catch (e: any) {
      toast.error(e?.message ?? (sendNow ? "GHL publish failed" : "GHL scheduling failed"));
    } finally {
      setSaveBusy(null);
    }
  };

  const runDelete = async (id: string) => {
    if (!confirm("Delete this draft?")) return;
    await del({ data: { id } });
    await qc.invalidateQueries({ queryKey: ["social-posts"] });
  };

  const runClear = async () => {
    if (!confirm("Delete every current draft? Published and scheduled posts will stay.")) return;
    try {
      const r = await clearDrafts();
      toast.success(`Removed ${r.deleted} draft${r.deleted === 1 ? "" : "s"}`);
      await qc.invalidateQueries({ queryKey: ["social-posts"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not clear drafts");
    }
  };

  return (
    <div>
      <SocialSubNav />
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-primary">AI Drafts</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              <strong>Generate now</strong> — 1 draft with an image preview.
              <br />
              <strong>Generate week</strong> — 1 post per day, Sunday through Saturday, on our loan programs, calculators, and resource pages, with a rotating voice (educational, practical tips, and lighter relatable days). Drafts stay in review until you send or schedule them.
              This set runs automatically every Saturday for the coming week, alongside the blog-linked drafts, and both stay visible here even before a social account is connected.

            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={runGenerate} disabled={!safeguardsOk || !!busy}>
              <Sparkles className="mr-2 h-4 w-4" />
              {busy === "now" ? "Generating…" : "Generate now"}
            </Button>
            <Button onClick={runGenerateWeek} disabled={!safeguardsOk || !!busy}>
              <Sparkles className="mr-2 h-4 w-4" />
              {busy === "week" ? "Generating week…" : "Generate week"}
            </Button>
            <Button variant="destructive" onClick={runClear} disabled={!!busy}>
              <Trash2 className="mr-2 h-4 w-4" />
              Clear drafts
            </Button>
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-primary">
            <ShieldCheck className="h-4 w-4" />
            Mortgage-compliance safeguards
          </div>
          <ul className="mt-2 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
            {MORTGAGE_SAFEGUARDS.map((s) => (
              <li key={s} className="flex gap-1.5">
                <span className="text-accent">✓</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
          <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs font-medium">
            <input
              type="checkbox"
              checked={safeguardsOk}
              onChange={(e) => setSafeguardsOk(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            I have reviewed the compliance safeguards above.
          </label>
        </div>
      </div>

      {accountsQ.data && !accountsQ.data.ok && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{accountsQ.data.error ?? "GHL accounts are not connected yet."}</span>
        </div>
      )}

      {accounts.length === 0 && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-brand-gold/40 bg-brand-gold/10 p-3 text-sm text-foreground">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
          <span>Weekly blog social drafts are ready for review. Connect an account before scheduling or publishing them.</span>
        </div>
      )}

      {weekDrafts.length > 0 && (
        <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3">
          <div className="text-sm font-semibold text-primary">
            This week: {weekDrafts.length} draft{weekDrafts.length === 1 ? "" : "s"} ({weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – {new Date(weekEnd.getTime() - 86400000).toLocaleDateString(undefined, { month: "short", day: "numeric" })})
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
            {Object.entries(weekByDay).map(([day, n]) => (
              <span key={day} className="rounded-full border border-border bg-background px-2 py-0.5">
                {day}: {n}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          {(["all", "draft", "scheduled", "published", "failed"] as const).map((status) => (
            <Button
              key={status}
              type="button"
              size="sm"
              variant={statusFilter === status ? "default" : "outline"}
              onClick={() => setStatusFilter(status)}
              className="capitalize"
            >
              {status === "all" ? "All" : status}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {([
            ["week", "This week"],
            ["next-week", "Next week"],
            ["all", "All dates"],
            ["unscheduled", "Unscheduled"],
          ] as const).map(([value, label]) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={datePreset === value ? "secondary" : "outline"}
              onClick={() => setDatePreset(value)}
            >
              {label}
            </Button>
          ))}
          {([
            ["all", "All sources"],
            ["blog", "From blog"],
            ["regular", "Weekly regular"],
            ["other", "Other"],
          ] as const).map(([value, label]) => (
            <Button
              key={`src-${value}`}
              type="button"
              size="sm"
              variant={sourceFilter === value ? "secondary" : "outline"}
              onClick={() => setSourceFilter(value)}
            >
              {label}
            </Button>
          ))}
          {categoryOptions.length > 0
            ? ["all", ...categoryOptions].map((value) => (
                <Button
                  key={`cat-${value}`}
                  type="button"
                  size="sm"
                  variant={categoryFilter === value ? "secondary" : "outline"}
                  onClick={() => setCategoryFilter(value)}
                >
                  {value === "all" ? "All types" : value.replace(/_/g, " ")}
                </Button>
              ))
            : null}
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {q.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : posts.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No posts match these filters. Click <strong>Generate week</strong> to create Sunday–Saturday drafts.
          </p>
        ) : (
          posts.map((p) => {
            const edit = edits[p.id] ?? {};
            const content = edit.body ?? p.body ?? "";
            const media = socialMediaUrls(edit.mediaUrl ?? p.media_urls ?? []);
             const compatible = new Set(compatibleAccountIds(p));
             const selectedAccounts = selectedAccountIds(p);
            return (
              <div key={p.id} className="rounded-lg border border-border bg-card p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full bg-primary px-2.5 py-1 font-semibold text-primary-foreground">
                    <CalendarClock className="mr-1 inline h-3.5 w-3.5" /> {dayBadge(p.scheduled_at)}
                  </span>
                  <span className="rounded-full border border-border px-2 py-0.5 font-semibold uppercase text-muted-foreground">
                    {p.status}
                  </span>
                  {(p.platforms ?? []).map((pl) => (
                    <span key={pl} className="rounded-full bg-accent px-2 py-0.5 font-semibold uppercase text-accent-foreground">
                      {pl}
                    </span>
                  ))}
                  {isBlogCampaign(p) ? (
                    <span className="rounded-full border border-brand-gold/60 bg-brand-gold/15 px-2 py-0.5 font-semibold uppercase text-brand-dark">
                      Blog post
                    </span>
                  ) : null}
                  {isRegularWeekly(p) ? (
                    <span className="rounded-full border border-border bg-muted px-2 py-0.5 font-semibold uppercase text-muted-foreground">
                      Weekly regular
                    </span>
                  ) : null}
                  {categoryLabelOf(p) ? (
                    <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 font-semibold text-primary">
                      {categoryLabelOf(p)}
                    </span>
                  ) : null}
                  {Number(p.featured_rank ?? 0) > 0 ? (
                    <span className="rounded-full bg-amber-500/20 px-2 py-0.5 font-semibold uppercase text-amber-700">
                      Featured this week
                    </span>
                  ) : null}
                  {p.external_ids?.source_blog_slug ? (
                    <a
                      href={`/blog/${p.external_ids.source_blog_slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full border border-border px-2 py-0.5 font-semibold text-muted-foreground hover:text-foreground"
                    >
                      Article: {String(p.external_ids.source_blog_slug)}
                    </a>
                  ) : null}
                  <span className="ml-auto text-muted-foreground">
                    Prepared {formatEtDateTime(p.created_at)}
                    {p.scheduled_at
                      ? ` · Recommended publish ${formatEtDateTime(p.scheduled_at)}`
                      : " · No recommended publish time yet"}
                  </span>
                </div>

                <div className="mt-4 grid gap-5 lg:grid-cols-[1fr_360px]">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Post body</Label>
                      <Textarea
                        rows={6}
                        value={content}
                        onChange={(e) => patch(p.id, { body: e.target.value })}
                        className="mt-1 font-mono text-sm"
                        maxLength={5000}
                      />
                      <p className="mt-1 text-[11px] text-muted-foreground">{content.length} characters</p>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          <Images className="mr-1 inline h-3.5 w-3.5" /> Image / video URLs
                        </Label>
                        <Textarea
                          rows={3}
                          value={edit.mediaUrl ?? (p.media_urls ?? []).join("\n")}
                          onChange={(e) => patch(p.id, { mediaUrl: e.target.value })}
                          placeholder="https://…jpg\nOne URL per line for carousel"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          <CalendarClock className="mr-1 inline h-3.5 w-3.5" /> Schedule time (ET)
                        </Label>
                        <Input
                          type="datetime-local"
                          value={edit.scheduleAt ?? toDateTimeLocal(p.scheduled_at)}
                          onChange={(e) => patch(p.id, { scheduleAt: e.target.value })}
                          className="mt-1"
                        />
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {recommendedTimesFor(p.scheduled_at).map((t) => (
                            <Button key={t.value} type="button" size="sm" variant="outline" onClick={() => patch(p.id, { scheduleAt: t.value })}>
                              {t.label}
                            </Button>
                          ))}
                        </div>
                        {p.schedule_rationale?.rationale ? (
                          <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
                            Why this time: {String(p.schedule_rationale.rationale)}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div>
                      <Label className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        <PlugZap className="h-3.5 w-3.5" /> Publish to GHL accounts
                      </Label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {accounts.length === 0 ? (
                          <p className="text-xs text-muted-foreground">No GHL social accounts loaded. Connect GHL first.</p>
                        ) : (
                           accounts.filter((a: any) => compatible.has(String(a.id))).map((a: any) => {
                            const checked = selectedAccounts.includes(a.id);
                            return (
                              <Button
                                key={a.id}
                                type="button"
                                size="sm"
                                variant={checked ? "default" : "outline"}
                                onClick={() =>
                                  patch(p.id, {
                                    selectedAccounts: checked
                                      ? selectedAccounts.filter((id) => id !== a.id)
                                      : [...selectedAccounts, a.id],
                                  })
                                }
                              >
                                {a.platform} · {a.name}
                              </Button>
                            );
                          })
                        )}
                      </div>
                    </div>

                    <div className="rounded-md border border-border bg-muted/30 p-3">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        <Wand2 className="mr-1 inline h-3.5 w-3.5" /> Ask AI to revise
                      </Label>
                      <div className="mt-2 flex gap-2">
                        <Input
                          placeholder="e.g. Shorter, warmer, stronger estimate CTA"
                          value={edit.instruction ?? ""}
                          onChange={(e) => patch(p.id, { instruction: e.target.value })}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" && !event.shiftKey) {
                              event.preventDefault();
                              void runRevise(p.id, content);
                            }
                          }}
                        />
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={reviseBusy === p.id || !(edit.instruction ?? "").trim()}
                          onClick={() => runRevise(p.id, content)}
                        >
                          {reviseBusy === p.id ? "Thinking…" : "Redo"}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <aside className="space-y-3">
                    <SocialPreview body={content} mediaUrls={media} onOpenMedia={(index) => setLightbox({ images: media, index })} />
                    {media.length > 1 && (
                      <div className="flex flex-wrap gap-2">
                        {media.slice(1).map((u, i) => (
                          <Button key={`${u}-${i}`} type="button" variant="outline" size="sm" onClick={() => setLightbox({ images: media, index: i + 1 })}>
                            Slide {i + 2}
                          </Button>
                        ))}
                      </div>
                    )}
                  </aside>
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => runDelete(p.id)}>
                    <Trash2 className="mr-1 h-4 w-4" /> Delete
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={anyBusy(p.id)}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      void runSave(p).catch(() => {});
                    }}
                  >
                    {isBusy(p.id, "save") ? "Saving…" : "Save edits"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={anyBusy(p.id)}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      void runSend(p, false);
                    }}
                  >
                    <CalendarClock className="mr-1 h-4 w-4" />{" "}
                    {isBusy(p.id, "schedule") ? "Scheduling…" : "Schedule in GHL"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    disabled={anyBusy(p.id)}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      void runSend(p, true);
                    }}
                  >
                    <Send className="mr-1 h-4 w-4" /> {isBusy(p.id, "post") ? "Posting…" : "Post now"}
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>
      {lightbox && (
        <MediaLightbox
          images={lightbox.images}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndex={(index) => setLightbox((state) => (state ? { ...state, index } : state))}
        />
      )}
    </div>
  );
}
